const LINKEDIN_ACTION_NEEDED_STATUSES = new Set(['reconnection_needed', 'no_recent_sync', 'not_logged_in_to_linkedin']);
const LINKEDIN_NOT_CONNECTED_STATUSES = new Set(['not_completed', 'not_connected']);

function getLinkedinStatus(raw, isDisconnected) {
  if (isDisconnected) return 'not_connected';
  if (LINKEDIN_ACTION_NEEDED_STATUSES.has(raw)) return 'action_needed';
  return raw;
}

function statusToIconState(status) {
  if (!status || LINKEDIN_NOT_CONNECTED_STATUSES.has(status)) return 'disabled';
  if (status === 'paused') return 'paused';
  if (LINKEDIN_ACTION_NEEDED_STATUSES.has(status) || status === 'permissions_missing') return 'warning';
  return 'active';
}

// Orange ("warning") only when health is 'stale' (>30d); transient states no
// longer drive the dot. Falls back to legacy raw-status when health is absent.
function linkedinIconState(raw, health) {
  if (!raw || LINKEDIN_NOT_CONNECTED_STATUSES.has(raw) || health === 'never') return 'disabled';
  if (raw === 'paused' || health === 'paused') return 'paused';
  if (health === 'stale') return 'warning';
  if (health == null) return statusToIconState(raw);
  return 'active';
}

export function normalizeLinkedinIntegration(member) {
  const raw = member.linkedinSyncStatus;
  const health = member.linkedinSyncHealth ?? null;
  const isDisconnected = !raw || LINKEDIN_NOT_CONNECTED_STATUSES.has(raw);

  return {
    type: 'linkedin',
    status: getLinkedinStatus(raw, isDisconnected),
    iconState: linkedinIconState(raw, health),
    health,
    contactsSynced: member.contactsSynced ?? null,
    lastSyncedAt: member.lastSyncedAt ?? null,
    extensionLastOnlineAt: member.extensionLastOnlineAt ?? null,
    linkedinSessionPresent: member.linkedinSessionPresent ?? null,
    rawStatus: isDisconnected ? null : raw,
    features: null,
  };
}

const GOOGLE_NOT_CONNECTED_STATUSES = new Set(['not_completed', 'not_connected']);
const GOOGLE_ACTION_NEEDED_STATUSES = new Set(['reconnection_needed']);

function getGoogleStatus(raw, isDisconnected) {
  if (isDisconnected) return 'not_connected';
  if (GOOGLE_ACTION_NEEDED_STATUSES.has(raw)) return 'action_needed';
  if (raw === 'in_sync') return 'active';
  return raw;
}

function googleIconState(status) {
  if (!status || GOOGLE_NOT_CONNECTED_STATUSES.has(status)) return 'disabled';
  if (status === 'permissions_missing' || GOOGLE_ACTION_NEEDED_STATUSES.has(status)) return 'warning';
  return 'active';
}

export function normalizeGoogleIntegration(member) {
  const raw = member.googleSyncStatus;
  const isDisconnected = !raw || GOOGLE_NOT_CONNECTED_STATUSES.has(raw);

  return {
    type: 'google',
    status: getGoogleStatus(raw, isDisconnected),
    iconState: googleIconState(raw),
    health: null,
    contactsSynced: member.googleContactsSynced ?? null,
    lastSyncedAt: member.googleLastSyncedAt ?? null,
    extensionLastOnlineAt: null,
    linkedinSessionPresent: null,
    rawStatus: isDisconnected ? null : raw,
    features: member.googleFeatures ?? [],
  };
}

// Stub adapter: the managers/allies API does not yet expose per-member Outlook
// integration state. Outlook visibility is gated by the orphan `outlook_sync`
// feature flag, so the adapter starts returning real data the moment that flag
// (and the matching backend fields) ship.
// eslint-disable-next-line no-unused-vars
export function normalizeOutlookIntegration(member) {
  return {
    type: 'outlook',
    status: 'not_connected',
    iconState: 'disabled',
    health: null,
    contactsSynced: null,
    lastSyncedAt: null,
    extensionLastOnlineAt: null,
    linkedinSessionPresent: null,
    rawStatus: null,
    features: null,
  };
}

export function getIntegrationsForMember(member, { hasGoogleSync, hasOutlookSync } = {}) {
  const integrations = [normalizeLinkedinIntegration(member)];
  if (hasGoogleSync) {
    integrations.push(normalizeGoogleIntegration(member));
  }
  if (hasOutlookSync) {
    integrations.push(normalizeOutlookIntegration(member));
  }
  return integrations;
}
